"use client"

import { useState, useMemo } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import CloseIcon from "@mui/icons-material/Close"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import TrendingDownIcon from "@mui/icons-material/TrendingDown"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import PersonIcon from "@mui/icons-material/Person"
import InventoryIcon from "@mui/icons-material/Inventory"
import SellIcon from "@mui/icons-material/Sell"
import AnalyticsIcon from "@mui/icons-material/Analytics"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Line,
  Area,
  AreaChart,
  ComposedChart,
} from "recharts"

const COLORS = [
  "#3B82F6", // Blue - Color primario para elementos principales
  "#10B981", // Emerald - Para métricas positivas y crecimiento
  "#F59E0B", // Amber - Para alertas y métricas de atención
  "#EF4444", // Red - Para métricas críticas o negativas
  "#8B5CF6", // Violet - Para diferenciación de categorías
  "#06B6D4", // Cyan - Para datos secundarios
  "#84CC16", // Lime - Para métricas de rendimiento
  "#F97316", // Orange - Para destacar elementos importantes
  "#EC4899", // Pink - Para categorías especiales
  "#6B7280", // Gray - Para elementos neutros
]

/**
 * COMPONENTE METRICCARD
 *
 * Propósito: Renderiza tarjetas de métricas con diseño profesional y efectos hover
 */
const MetricCard = ({ title, value, subtitle, icon: Icon, color = "primary", trend }) => {
  const theme = useTheme()

  return (
    <Card
      elevation={2}
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${theme.palette[color].main}08 0%, ${theme.palette[color].main}15 100%)`,
        border: `1px solid ${theme.palette[color].main}20`,
        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette[color].main, mb: 0.5 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: `${theme.palette[color].main}15`,
              color: theme.palette[color].main,
              width: 48,
              height: 48,
            }}
          >
            <Icon />
          </Avatar>
        </Box>
        {trend && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
            {trend > 0 ? (
              <TrendingUpIcon sx={{ color: "success.main", fontSize: 16, mr: 0.5 }} />
            ) : (
              <TrendingDownIcon sx={{ color: "error.main", fontSize: 16, mr: 0.5 }} />
            )}
            <Typography
              variant="caption"
              sx={{
                color: trend > 0 ? "success.main" : "error.main",
                fontWeight: 600,
              }}
            >
              {Math.abs(trend)}% vs período anterior
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * COMPONENTE TABPANEL
 */
function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`chart-tabpanel-${index}`}
      aria-labelledby={`chart-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

/**
 * FUNCIÓN A11YPROPS
 */
function a11yProps(index) {
  return {
    id: `chart-tab-${index}`,
    "aria-controls": `chart-tabpanel-${index}`,
  }
}

/**
 * COMPONENTE PRINCIPAL: GRAFICASDETALLES PRESUPUESTOS MODAL
 */
const GraficasDetallesPresupuestosModal = ({ open, handleClose, data, loading = false }) => {
  const theme = useTheme()
  const [tabValue, setTabValue] = useState(0)

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  /**
   * CÁLCULO DE MÉTRICAS GENERALES
   */
  const metricsGenerales = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return {
        totalPresupuestos: 0,
        totalItems: 0,
        transaccionesUnicas: 0,
        clientesUnicos: 0,
        itemsUnicos: 0,
        ticketPromedio: 0,
        itemsPorTransaccion: 0,
      }
    }

    const totalPresupuestos = data.reduce((sum, item) => sum + (item.montoTotal || 0), 0)
    const totalItems = data.reduce((sum, item) => sum + (item.cantidad || 0), 0)
    const transaccionesUnicas = new Set(data.map((item) => item.Transaccion?.id)).size
    const clientesUnicos = new Set(data.map((item) => item.Transaccion?.entidad?.id)).size
    const itemsUnicos = new Set(data.map((item) => item.idItem)).size
    const ticketPromedio = transaccionesUnicas > 0 ? totalPresupuestos / transaccionesUnicas : 0
    const itemsPorTransaccion = transaccionesUnicas > 0 ? totalItems / transaccionesUnicas : 0

    return {
      totalPresupuestos,
      totalItems,
      transaccionesUnicas,
      clientesUnicos,
      itemsUnicos,
      ticketPromedio,
      itemsPorTransaccion,
    }
  }, [data])

  /**
   * FUNCIÓN: PROCESARTOP10ITEMS
   */
  const procesarTop10Items = () => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return []
    }
    
    const itemData = {}

    data.forEach((item) => {
      const itemNombre = item.item?.descripcion || "Sin descripción"
      const itemCodigo = item.item?.codigo || ""
      const itemKey = `${itemCodigo} - ${itemNombre}`

      if (!itemData[itemKey]) {
        itemData[itemKey] = {
          nombre: itemKey,
          cantidadPresupuestada: 0,
          montoTotal: 0,
          precioPromedio: 0,
          transacciones: new Set(),
          margenBruto: 0,
        }
      }

      itemData[itemKey].cantidadPresupuestada += item.cantidad || 0
      itemData[itemKey].montoTotal += item.montoTotal || 0
      itemData[itemKey].transacciones.add(item.Transaccion?.id)
    })

    // Calcular métricas adicionales para cada item
    Object.values(itemData).forEach((item) => {
      item.precioPromedio = item.cantidadPresupuestada > 0 ? item.montoTotal / item.cantidadPresupuestada : 0
      item.transaccionesCount = item.transacciones.size
      item.transacciones = undefined // Limpiar para evitar problemas de serialización
    })

    return Object.values(itemData)
      .sort((a, b) => b.cantidadPresupuestada - a.cantidadPresupuestada)
      .slice(0, 10)
  }

  /**
   * FUNCIÓN: PROCESARTOP10CLIENTESPORFECHA
   */
  const procesarTop10ClientesPorFecha = () => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return []
    }
    
    const clienteData = {}

    data.forEach((item) => {
      const clienteNombre =
        `${item.Transaccion?.entidad?.descripcion || "Sin nombre"} ${item.Transaccion?.entidad?.apellido || ""}`.trim()
      const fecha = new Date(item.Transaccion?.fecha).toLocaleDateString("es-AR")

      if (!clienteData[clienteNombre]) {
        clienteData[clienteNombre] = {
          nombre: clienteNombre,
          totalPresupuestos: 0,
          cantidadPresupuestos: 0,
          cantidadItems: 0,
          ultimoPresupuesto: fecha,
          fechas: new Set(),
          transacciones: new Set(),
        }
      }

      clienteData[clienteNombre].totalPresupuestos += item.montoTotal || 0
      clienteData[clienteNombre].cantidadItems += item.cantidad || 0
      clienteData[clienteNombre].fechas.add(fecha)
      clienteData[clienteNombre].transacciones.add(item.Transaccion?.id)

      // Actualizar último presupuesto comparando fechas
      if (
        new Date(fecha.split("/").reverse().join("-")) >
        new Date(clienteData[clienteNombre].ultimoPresupuesto.split("/").reverse().join("-"))
      ) {
        clienteData[clienteNombre].ultimoPresupuesto = fecha
      }
    })

    return Object.values(clienteData)
      .map((cliente) => ({
        ...cliente,
        diasActivos: cliente.fechas.size,
        cantidadPresupuestos: cliente.transacciones.size,
        ticketPromedio: cliente.cantidadPresupuestos > 0 ? cliente.totalPresupuestos / cliente.cantidadPresupuestos : 0,
        itemsPorPresupuesto: cliente.cantidadPresupuestos > 0 ? cliente.cantidadItems / cliente.cantidadPresupuestos : 0,
        fechas: undefined,
        transacciones: undefined,
      }))
      .sort((a, b) => b.totalPresupuestos - a.totalPresupuestos)
      .slice(0, 10)
  }

  /**
   * FUNCIÓN: PROCESARMEDIOSDEPAGO
   */
  const procesarMediosDePago = () => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return { medios: [], tipos: [] }
    }
    
    const mediosData = {}
    const tiposData = {}

    data.forEach((item) => {
      const transaccionPagos = item.Transaccion?.transaccionPago || []

      transaccionPagos.forEach((tp) => {
        const pago = tp.pago
        if (pago?.medioDePago) {
          const medioNombre = pago.medioDePago.descripcion
          const tipoMedio = pago.medioDePago.tipoMedioDePago?.descripcion || "Sin tipo"
          // Cálculo proporcional del monto del item en este medio de pago
          const montoItem = pago.montoTotal * (pago.cotizacion || 1) * (item.montoTotal / item.Transaccion.montoTotal)

          // Agrupación por medio específico
          if (!mediosData[medioNombre]) {
            mediosData[medioNombre] = {
              nombre: medioNombre,
              tipo: tipoMedio,
              cantidadTransacciones: 0,
              montoTotal: 0,
              transacciones: new Set(),
            }
          }

          mediosData[medioNombre].transacciones.add(item.Transaccion?.id)
          mediosData[medioNombre].montoTotal += montoItem

          // Agrupación por tipo de medio
          if (!tiposData[tipoMedio]) {
            tiposData[tipoMedio] = {
              nombre: tipoMedio,
              cantidadTransacciones: 0,
              montoTotal: 0,
              transacciones: new Set(),
            }
          }

          tiposData[tipoMedio].transacciones.add(item.Transaccion?.id)
          tiposData[tipoMedio].montoTotal += montoItem
        }
      })
    })

    // Procesamiento final de datos
    const mediosFinales = Object.values(mediosData)
      .map((medio) => ({
        ...medio,
        cantidadTransacciones: medio.transacciones.size,
        transacciones: undefined,
      }))
      .sort((a, b) => b.cantidadTransacciones - a.cantidadTransacciones)

    const tiposFinales = Object.values(tiposData)
      .map((tipo) => ({
        ...tipo,
        cantidadTransacciones: tipo.transacciones.size,
        transacciones: undefined,
      }))
      .sort((a, b) => b.montoTotal - a.montoTotal)

    return { medios: mediosFinales, tipos: tiposFinales }
  }

  /**
   * FUNCIÓN: PROCESARTOPVENDEDORES
   */
  const procesarTopVendedores = () => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return []
    }
    
    const vendedorData = {}

    data.forEach((item) => {
      const vendedorNombre = item.Transaccion?.usuario?.usuario || "Sin vendedor"

      if (!vendedorData[vendedorNombre]) {
        vendedorData[vendedorNombre] = {
          nombre: vendedorNombre,
          totalPresupuestos: 0,
          cantidadItems: 0,
          transacciones: new Set(),
          clientes: new Set(),
          itemsUnicos: new Set(),
        }
      }

      vendedorData[vendedorNombre].totalPresupuestos += item.montoTotal || 0
      vendedorData[vendedorNombre].cantidadItems += item.cantidad || 0
      vendedorData[vendedorNombre].transacciones.add(item.Transaccion?.id)
      vendedorData[vendedorNombre].clientes.add(item.Transaccion?.entidad?.id)
      vendedorData[vendedorNombre].itemsUnicos.add(item.idItem)
    })

    return Object.values(vendedorData)
      .map((vendedor) => ({
        ...vendedor,
        cantidadTransacciones: vendedor.transacciones.size,
        clientesUnicos: vendedor.clientes.size,
        itemsUnicos: vendedor.itemsUnicos.size,
        ticketPromedio: vendedor.cantidadTransacciones > 0 ? vendedor.totalPresupuestos / vendedor.cantidadTransacciones : 0,
        itemsPorTransaccion:
          vendedor.cantidadTransacciones > 0 ? vendedor.cantidadItems / vendedor.cantidadTransacciones : 0,
        transacciones: undefined,
        clientes: undefined,
      }))
      .sort((a, b) => b.totalPresupuestos - a.totalPresupuestos)
  }

  /**
   * FUNCIÓN: PROCESARANALISISTEMPOAL
   */
  const procesarAnalisisTemporal = () => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return []
    }
    
    const presupuestosPorDia = {}

    data.forEach((item) => {
      const fecha = new Date(item.Transaccion?.fecha).toLocaleDateString("es-AR")

      if (!presupuestosPorDia[fecha]) {
        presupuestosPorDia[fecha] = {
          fecha,
          totalPresupuestos: 0,
          cantidadItems: 0,
          transacciones: new Set(),
        }
      }

      presupuestosPorDia[fecha].totalPresupuestos += item.montoTotal || 0
      presupuestosPorDia[fecha].cantidadItems += item.cantidad || 0
      presupuestosPorDia[fecha].transacciones.add(item.Transaccion?.id)
    })

    return Object.values(presupuestosPorDia)
      .map((dia) => ({
        ...dia,
        cantidadTransacciones: dia.transacciones.size,
        ticketPromedio: dia.cantidadTransacciones > 0 ? dia.totalPresupuestos / dia.cantidadTransacciones : 0,
        transacciones: undefined,
      }))
      .sort(
        (a, b) => new Date(a.fecha.split("/").reverse().join("-")) - new Date(b.fecha.split("/").reverse().join("-")),
      )
  }

  // Procesamiento de todos los datos usando las funciones definidas
  const datosTop10Items = procesarTop10Items()
  const datosTop10Clientes = procesarTop10ClientesPorFecha()
  const { medios: datosMediosPago, tipos: datosTiposPago } = procesarMediosDePago()
  const datosTopVendedores = procesarTopVendedores()
  const datosAnalisisTemporal = procesarAnalisisTemporal()

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      {/* Header del modal con gradiente y branding */}
      <DialogTitle
        sx={{
          m: 0,
          p: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <AnalyticsIcon sx={{ fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Dashboard de Análisis de Presupuestos
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Análisis detallado de rendimiento y métricas de presupuestos
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: "white",
            "&:hover": {
              bgcolor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, position: "relative", minHeight: 600 }}>
        {loading ? (
          // Estado de carga con spinner y mensajes informativos
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 600,
              gap: 3,
            }}
          >
            <CircularProgress size={80} color="primary" thickness={4} />
            <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 600 }}>
              Procesando datos de análisis...
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", maxWidth: 400 }}>
              Analizando patrones de presupuestos, comportamiento de clientes y métricas de rendimiento
            </Typography>
          </Box>
        ) : (
          <>
            {/* Panel de resumen ejecutivo con KPIs principales */}
            <Box sx={{ p: 3, bgcolor: "grey.50" }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "text.primary" }}>
                Resumen Ejecutivo - Presupuestos
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="Total Presupuestado"
                    value={`$${metricsGenerales.totalPresupuestos?.toLocaleString("es-AR") || "0"}`}
                    subtitle={`${metricsGenerales.transaccionesUnicas} presupuestos`}
                    icon={AttachMoneyIcon}
                    color="success"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="Items Presupuestados"
                    value={metricsGenerales.totalItems?.toLocaleString("es-AR") || "0"}
                    subtitle={`${metricsGenerales.itemsUnicos} productos únicos`}
                    icon={InventoryIcon}
                    color="primary"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="Ticket Promedio"
                    value={`$${metricsGenerales.ticketPromedio?.toLocaleString("es-AR", { maximumFractionDigits: 0 }) || "0"}`}
                    subtitle={`${metricsGenerales.itemsPorTransaccion?.toFixed(1)} items/presupuesto`}
                    icon={ShoppingCartIcon}
                    color="warning"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="Clientes Únicos"
                    value={metricsGenerales.clientesUnicos?.toLocaleString("es-AR") || "0"}
                    subtitle="Base de clientes presupuestada"
                    icon={PersonIcon}
                    color="info"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Sistema de pestañas para navegación entre análisis */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="chart tabs"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  "& .MuiTab-root": {
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "0.95rem",
                  },
                }}
              >
                <Tab label="📊 Top Items" {...a11yProps(0)} />
                <Tab label="👥 Análisis de Clientes" {...a11yProps(1)} />
                <Tab label="💳 Medios de Pago" {...a11yProps(2)} />
                <Tab label="🏆 Performance Vendedores" {...a11yProps(3)} />
                <Tab label="📈 Tendencias Temporales" {...a11yProps(4)} />
              </Tabs>
            </Box>

            {/* PESTAÑA 1: ANÁLISIS DE TOP ITEMS */}
            <TabPanel value={tabValue} index={0}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                Top 10 Items Más Presupuestados
              </Typography>

              <Grid container spacing={3}>
                {/* Gráfico principal: ComposedChart con barras y línea */}
                <Grid item xs={12} lg={8}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Cantidad vs Monto de Presupuestos
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={datosTop10Items}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={120} interval={0} fontSize={11} />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === "montoTotal") return [`$${value.toLocaleString()}`, "Monto Total"]
                            if (name === "cantidadPresupuestada") return [value, "Cantidad Presupuestada"]
                            if (name === "precioPromedio") return [`$${value.toFixed(2)}`, "Precio Promedio"]
                            return [value, name]
                          }}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Legend />
                        <Bar yAxisId="left" dataKey="cantidadPresupuestada" fill={COLORS[0]} name="Cantidad Presupuestada" />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="precioPromedio"
                          stroke={COLORS[2]}
                          strokeWidth={3}
                          name="Precio Promedio"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Panel lateral: Top 5 por monto */}
                <Grid item xs={12} lg={4}>
                  <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Top 5 por Monto
                    </Typography>
                    <List>
                      {datosTop10Items.slice(0, 5).map((item, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: COLORS[index], width: 32, height: 32, fontSize: "0.875rem" }}>
                              {index + 1}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {item.nombre.length > 30 ? `${item.nombre.substring(0, 30)}...` : item.nombre}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                  ${item.montoTotal.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  {item.cantidadPresupuestada} unidades
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* PESTAÑA 2: ANÁLISIS DE CLIENTES */}
            <TabPanel value={tabValue} index={1}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                Análisis de Comportamiento de Clientes
              </Typography>

              <Grid container spacing={3}>
                {/* Gráfico principal: Presupuestos vs Frecuencia */}
                <Grid item xs={12} lg={8}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Top Clientes - Presupuestos vs Frecuencia
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={datosTop10Clientes}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={120} interval={0} fontSize={11} />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === "totalPresupuestos") return [`$${value.toLocaleString()}`, "Total Presupuestos"]
                            if (name === "cantidadPresupuestos") return [value, "Cantidad Presupuestos"]
                            if (name === "ticketPromedio") return [`$${value.toLocaleString()}`, "Ticket Promedio"]
                            if (name === "diasActivos") return [value, "Días Activos"]
                            return [value, name]
                          }}
                          labelFormatter={(label, payload) => {
                            const data = payload?.[0]?.payload
                            return data ? `${label} - Último presupuesto: ${data.ultimoPresupuesto}` : label
                          }}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Legend />
                        <Bar yAxisId="left" dataKey="totalPresupuestos" fill={COLORS[1]} name="Total Presupuestos" />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="cantidadPresupuestos"
                          stroke={COLORS[3]}
                          strokeWidth={3}
                          name="Cantidad Presupuestos"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Panel lateral: Clientes VIP */}
                <Grid item xs={12} lg={4}>
                  <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Clientes VIP
                    </Typography>
                    <List>
                      {datosTop10Clientes.slice(0, 5).map((cliente, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: COLORS[index + 1], width: 32, height: 32, fontSize: "0.875rem" }}>
                              {index + 1}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {cliente.nombre.length > 25 ? `${cliente.nombre.substring(0, 25)}...` : cliente.nombre}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                  ${cliente.totalPresupuestos.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  {cliente.cantidadPresupuestos} presupuestos
                                </Typography>
                                <br />
                                <Typography variant="caption" color="primary.main">
                                  Ticket: ${cliente.ticketPromedio.toLocaleString()}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* PESTAÑA 3: ANÁLISIS DE MEDIOS DE PAGO */}
            <TabPanel value={tabValue} index={2}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                Análisis de Medios de Pago
              </Typography>

              <Grid container spacing={3}>
                {/* Gráfico de barras: Transacciones por medio */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Transacciones por Medio de Pago
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={datosMediosPago}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={80} interval={0} fontSize={11} />
                        <YAxis />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === "cantidadTransacciones") return [value, "Transacciones"]
                            return [value, name]
                          }}
                          labelFormatter={(label, payload) => {
                            const data = payload?.[0]?.payload
                            return data ? `${label} (${data.tipo})` : label
                          }}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Bar dataKey="cantidadTransacciones" fill={COLORS[0]} name="Transacciones" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Gráfico circular: Distribución por monto */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Distribución por Monto
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={datosMediosPago}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ nombre, montoTotal, percent }) => `${nombre}: ${(percent * 100).toFixed(1)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="montoTotal"
                        >
                          {datosMediosPago.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => [`$${value.toLocaleString()}`, "Monto Total"]}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Gráfico horizontal: Análisis por tipo de medio */}
                <Grid item xs={12}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Análisis por Tipo de Medio de Pago
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={datosTiposPago} layout="horizontal">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" />
                        <YAxis dataKey="nombre" type="category" width={100} />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === "montoTotal") return [`$${value.toLocaleString()}`, "Monto Total"]
                            if (name === "cantidadTransacciones") return [value, "Transacciones"]
                            return [value, name]
                          }}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Legend />
                        <Bar dataKey="montoTotal" fill={COLORS[1]} name="Monto Total" />
                        <Bar dataKey="cantidadTransacciones" fill={COLORS[4]} name="Transacciones" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* PESTAÑA 4: PERFORMANCE DE VENDEDORES */}
            <TabPanel value={tabValue} index={3}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                Performance de Vendedores
              </Typography>

              <Grid container spacing={3}>
                {/* Gráfico combinado: Métricas múltiples por vendedor */}
                <Grid item xs={12} lg={8}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Métricas de Performance por Vendedor
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={datosTopVendedores}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={100} interval={0} fontSize={11} />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === "totalPresupuestos") return [`$${value.toLocaleString()}`, "Total Presupuestos"]
                            if (name === "cantidadItems") return [value, "Items Presupuestados"]
                            if (name === "cantidadTransacciones") return [value, "Transacciones"]
                            if (name === "clientesUnicos") return [value, "Clientes Únicos"]
                            if (name === "ticketPromedio") return [`$${value.toLocaleString()}`, "Ticket Promedio"]
                            return [value, name]
                          }}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Legend />
                        <Bar yAxisId="left" dataKey="totalPresupuestos" fill={COLORS[0]} name="Total Presupuestos" />
                        <Bar yAxisId="left" dataKey="cantidadTransacciones" fill={COLORS[2]} name="Transacciones" />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="clientesUnicos"
                          stroke={COLORS[3]}
                          strokeWidth={3}
                          name="Clientes Únicos"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Panel lateral: Top performers */}
                <Grid item xs={12} lg={4}>
                  <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Top Performers
                    </Typography>
                    <List>
                      {datosTopVendedores.slice(0, 5).map((vendedor, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: COLORS[index + 2], width: 32, height: 32, fontSize: "0.875rem" }}>
                              {index + 1}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {vendedor.nombre}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                  ${vendedor.totalPresupuestos.toLocaleString()}
                                </Typography>
                                <br />
                                <Typography variant="caption" color="text.secondary">
                                  {vendedor.cantidadTransacciones} presupuestos • {vendedor.clientesUnicos} clientes
                                </Typography>
                                <br />
                                <Typography variant="caption" color="primary.main">
                                  Ticket: ${vendedor.ticketPromedio.toLocaleString()}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* PESTAÑA 5: ANÁLISIS TEMPORAL */}
            <TabPanel value={tabValue} index={4}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                Análisis de Tendencias Temporales
              </Typography>

              {/* Gráfico de área: Evolución temporal */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Evolución de Presupuestos por Día
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={datosAnalisisTemporal}>
                    <defs>
                      <linearGradient id="colorPresupuestos" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS[1]} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={COLORS[1]} stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id="colorTransacciones" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS[0]} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={COLORS[0]} stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="fecha" angle={-45} textAnchor="end" height={80} fontSize={11} />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip
                      formatter={(value, name) => {
                        if (name === "totalPresupuestos") return [`$${value.toLocaleString()}`, "Total Presupuestos"]
                        if (name === "cantidadTransacciones") return [value, "Transacciones"]
                        if (name === "ticketPromedio") return [`$${value.toLocaleString()}`, "Ticket Promedio"]
                        return [value, name]
                      }}
                      contentStyle={{
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="totalPresupuestos"
                      stroke={COLORS[1]}
                      fillOpacity={1}
                      fill="url(#colorPresupuestos)"
                      name="Total Presupuestos"
                      strokeWidth={2}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="cantidadTransacciones"
                      stroke={COLORS[0]}
                      strokeWidth={3}
                      name="Transacciones"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>

              {/* Métricas de resumen temporal */}
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="Día con Mayor Presupuesto"
                    value={
                      datosAnalisisTemporal.length > 0
                        ? datosAnalisisTemporal.reduce((max, dia) => (dia.totalPresupuestos > max.totalPresupuestos ? dia : max))
                            .fecha
                        : "N/A"
                    }
                    subtitle={
                      datosAnalisisTemporal.length > 0
                        ? `$${datosAnalisisTemporal.reduce((max, dia) => (dia.totalPresupuestos > max.totalPresupuestos ? dia : max)).totalPresupuestos.toLocaleString()}`
                        : "N/A"
                    }
                    icon={TrendingUpIcon}
                    color="success"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="Promedio Diario"
                    value={
                      datosAnalisisTemporal.length > 0
                        ? `$${(datosAnalisisTemporal.reduce((sum, dia) => sum + dia.totalPresupuestos, 0) / datosAnalisisTemporal.length).toLocaleString()}`
                        : "$0"
                    }
                    subtitle={
                      datosAnalisisTemporal.length > 0
                        ? `${(datosAnalisisTemporal.reduce((sum, dia) => sum + dia.cantidadTransacciones, 0) / datosAnalisisTemporal.length).toFixed(1)} presupuestos/día`
                        : "N/A"
                    }
                    icon={AnalyticsIcon}
                    color="info"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="Días Activos"
                    value={datosAnalisisTemporal.length}
                    subtitle="Período analizado"
                    icon={SellIcon}
                    color="warning"
                  />
                </Grid>
              </Grid>
            </TabPanel>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default GraficasDetallesPresupuestosModal
